import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClockIcon, 
  ArrowPathIcon, 
  HeartIcon,
  ShoppingBagIcon,
  MapPinIcon,
  PhoneIcon,
  SpeakerWaveIcon,
  TrashIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { showSuccessNotification, showErrorNotification, showWarningNotification, showInfoNotification } from './NotificationManager';
import MessageDisplay from './MessageDisplay';
import OrderLoader from './OrderLoader';
import { jwtDecode } from 'jwt-decode';

const API_BASE = 'https://mongobyte-hc73.onrender.com/api/v1';

const REORDER_API = {
  baseUrl: API_BASE,
  endpoints: {
    saveQuickReorder: '/advanced-orders/quick-reorder',
    getQuickReorders: '/advanced-orders/quick-reorders',
    executeQuickReorder: (id) => `/advanced-orders/quick-reorder/${id}/execute`,
    previewReorder: '/orders/preview-reorder',
    regularReorder: '/orders/by-reference'
  }
};

const ReorderSystem = ({ userToken, onOrderSuccess }) => {
  const [activeTab, setActiveTab] = useState('quick');
  const [savedOrders, setSavedOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  
  // Landmarks for delivery
  const [universityLandmarks, setUniversityLandmarks] = useState([]);
  const [landmarksLoading, setLandmarksLoading] = useState(false);
  
  // Regular reorder states
  const [orderCustomId, setOrderCustomId] = useState('');
  const [isExecutingReorder, setIsExecutingReorder] = useState(false);
  
  // Preview functionality states
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // Delete functionality states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reorderToDelete, setReorderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);
  
  // Delivery details override
  const [deliveryOverride, setDeliveryOverride] = useState({
    location: '',
    phoneNumber: '',
    nearestLandmark: '',
    note: ''
  });
  const [showDeliveryOverride, setShowDeliveryOverride] = useState(false);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${userToken}`
  }), [userToken]);

  const showMessage = useCallback((type, title, content, details = null) => {
    const message = details ? `${content}. ${details}` : content;
    
    switch (type) {
      case 'success':
        showSuccessNotification(message);
        break;
      case 'error':
        showErrorNotification(message);
        break;
      case 'warning':
        showWarningNotification(message);
        break;
      case 'info':
      default:
        showInfoNotification(message);
        break;
    }
  }, []);

  // Common Error Handler with detailed response handling
  const handleReorderError = (error, result) => {
    console.error('Reorder error:', error, result);
    
    if (result) {
      // Handle specific API errors
      if (result.unavailableMeals && result.unavailableMeals.length > 0) {
        const unavailableList = result.unavailableMeals
          .map(meal => `• ${meal.name} (${meal.reason})`)
          .join('\n');
        
        showMessage(
          'error',
          'Some meals are no longer available',
          unavailableList,
          result.suggestions?.join('\n') || 'Please try selecting different items'
        );
        return;
      }

      if (result.missingFields && result.missingFields.length > 0) {
        showMessage(
          'warning',
          'Profile Incomplete',
          `Please update your profile with: ${result.missingFields.join(', ')}`,
          result.suggestion || 'Go to your profile settings and add the missing information'
        );
        return;
      }

      if (result.shortfall) {
        showMessage(
          'error',
          'Insufficient Balance',
          `You need ₦${result.shortfall} more to place this order.`,
          `Current Balance: ₦${result.currentBalance?.toLocaleString() || 0}`
        );
        return;
      }

      if (result.invalidMeals && result.invalidMeals.length > 0) {
        showMessage(
          'error',
          'Invalid meal data in override',
          'Some meals in your override are invalid',
          'Please check the meal IDs and quantities'
        );
        return;
      }
    }

    // Fallback error handling based on status codes
    switch (error.response?.status) {
      case 400:
        showMessage('error', 'Invalid Request', result?.message || 'Please check your input and try again.');
        break;
      case 401:
        showMessage('error', 'Authentication Required', 'Please log in again.');
        break;
      case 404:
        showMessage('error', 'Order Not Found', 'Please check the order ID and try again.');
        break;
      case 500:
        showMessage('error', 'Server Error', 'Please try again later.');
        break;
      default:
        showMessage('error', 'Something Went Wrong', result?.message || error.message || 'Please try again.');
    }
  };

  // Fetch saved quick reorders
  const fetchQuickReorders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${REORDER_API.baseUrl}${REORDER_API.endpoints.getQuickReorders}`, {
        headers
      });
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: Failed to fetch quick reorders`);
      }
      
      if (result.success) {
        setSavedOrders(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch quick reorders');
      }
    } catch (error) {
      console.error('Error fetching quick reorders:', error);
      setError(error.message);
      // Don't show message if it's just empty data
      if (!error.message.includes('No quick reorders found')) {
        showMessage('error', 'Failed to Load', 'Unable to load saved orders');
      }
    } finally {
      setIsLoading(false);
    }
  }, [headers, showMessage]);

  // Fetch landmarks for the user's university
  const fetchLandmarks = useCallback(async (universityId) => {  
    // console.log("trying...")
    if (!universityId) {
      setUniversityLandmarks([]);
      return;
    }
    setLandmarksLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/universities/${universityId}/landmarks`,
        { headers }
      );  
      if (response.ok) {
        const data = await response.json();
        setUniversityLandmarks(data.landmarks || []);
      } else {
        setUniversityLandmarks([]);
      }
    } catch (error) {
      console.error('Error fetching landmarks:', error);
      setUniversityLandmarks([]);
    } finally {
      setLandmarksLoading(false);
    }
  }, [headers]);

  // Fetch landmarks when component mounts (get user's university from token)
  useEffect(() => {
    // console.log(userToken)
    if (userToken) {
      try {
        // console.log(userToken)
        const decoded = jwtDecode(userToken)
        if (decoded) {
          fetchLandmarks(decoded.university?._id||decoded.user?.university?._id);
          // console.log(decoded)
          // console.log(decoded.user.university._id)
        }
      } catch (error) {
        console.error('Error decoding token for university:', error);
      }
    }
  }, [userToken, fetchLandmarks]);

  // Execute a quick reorder
  const executeQuickReorder = async (reorderId, orderName) => {
    setIsExecutingReorder(true);
    try {
      const deliveryDetails = showDeliveryOverride ? deliveryOverride : {};
      
      const response = await fetch(
        `${REORDER_API.baseUrl}${REORDER_API.endpoints.executeQuickReorder(reorderId)}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(deliveryDetails)
        }
      );
      
      const result = await response.json();
      
      if (!response.ok) {
        handleReorderError({ response: { status: response.status } }, result);
        return;
      }
      
      if (result.success) {
        const order = result.data?.order;
        const quickReorderUsed = result.data?.quickReorderUsed;
        const pricing = result.data?.pricing;
        
        // Create detailed success message with order details
        const mealNames = order?.meals?.map(m => m.meal?.name).join(', ') || 'items';
        const restaurant = order?.restaurant?.name || 'restaurant';
        const totalPrice = pricing?.finalPrice || order?.totalPrice || 0;
        
        showMessage(
          'success',
          `🎉 "${orderName}" Reordered Successfully!`,
          `${mealNames} from ${restaurant}`,
          `Order #${order?.customId} • Total: ₦${totalPrice.toLocaleString()} • Status: ${order?.status}`
        );
        
        if (onOrderSuccess) {
          onOrderSuccess(order);
        }
        
        // Reset form
        setDeliveryOverride({ location: '', phoneNumber: '', nearestLandmark: '', note: '' });
        setShowDeliveryOverride(false);
        
        // Refresh the list to update usage counts
        fetchQuickReorders();
      } else {
        handleReorderError({}, result);
      }
    } catch (error) {
      console.error('Error executing quick reorder:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showMessage('error', 'Network Error', 'Please check your connection and try again.');
      } else {
        showMessage('error', 'Reorder Failed', error.message || 'Unable to complete reorder');
      }
    } finally {
      setIsExecutingReorder(false);
    }
  };

  // Delete quick reorder function
  const deleteQuickReorder = async (reorderId) => {
    setIsDeleting(reorderId);
    try {
      const response = await fetch(`${API_BASE}/advanced-orders/quick-reorder/${reorderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete quick reorder');
      }
      
      if (result.success) {
        showMessage('success', 'Deleted Successfully', `"${result.data.deletedQuickReorder.name}" has been removed from your quick reorders.`);
        setShowDeleteModal(false);
        setReorderToDelete(null);
        
        // Remove the deleted reorder from the list
        setSavedOrders(prev => prev.filter(reorder => reorder._id !== reorderId));
        
      } else {
        throw new Error(result.message || 'Failed to delete quick reorder');
      }
    } catch (error) {
      console.error('Error deleting quick reorder:', error);
      showMessage('error', 'Delete Failed', error.message || 'Failed to delete quick reorder');
    } finally {
      setIsDeleting(null);
    }
  };

  // Preview reorder function
  const previewReorder = async () => {
    if (!orderCustomId.trim()) {
      showMessage('warning', 'Order ID Required', 'Please enter an order ID');
      return;
    }

    setIsPreviewLoading(true);
    try {
      const requestBody = {
        orderCustomId: orderCustomId.trim()
      };

      // Add delivery details if provided
      if (showDeliveryOverride) {
        if (deliveryOverride.location) requestBody.location = deliveryOverride.location;
        if (deliveryOverride.phoneNumber) requestBody.phoneNumber = deliveryOverride.phoneNumber;
        if (deliveryOverride.nearestLandmark) requestBody.nearestLandmark = deliveryOverride.nearestLandmark;
        if (deliveryOverride.note) requestBody.note = deliveryOverride.note;
      }

      const response = await fetch(
        `${REORDER_API.baseUrl}${REORDER_API.endpoints.previewReorder}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        }
      );
      
      const result = await response.json();
      
      if (!response.ok) {
        handleReorderError({ response: { status: response.status } }, result);
        return;
      }
      
      if (result.success && result.preview) {
        setPreviewData(result.preview);
        setShowPreview(true);
        
        // Show any issues
        if (!result.preview.canProceed.status) {
          showMessage(
            'warning',
            'Issues Found',
            result.preview.canProceed.issues.join('\n'),
            'Review the preview below for details'
          );
        } else {
          showMessage(
            'success',
            'Preview Ready',
            'Order preview loaded successfully',
            'Review details and proceed with order'
          );
        }
      } else {
        throw new Error(result.message || 'Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showMessage('error', 'Network Error', 'Please check your connection and try again.');
      } else {
        showMessage('error', 'Preview Failed', error.message || 'Unable to generate preview');
      }
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Execute regular reorder
  const executeRegularReorder = async () => {
    if (!orderCustomId.trim()) {
      showMessage('warning', 'Order ID Required', 'Please enter an order ID');
      return;
    }

    setIsExecutingReorder(true);
    try {
      const requestBody = {
        orderCustomId: orderCustomId.trim()
      };

      // Add delivery details if provided (only when explicitly overriding)
      if (showDeliveryOverride) {
        if (deliveryOverride.location) requestBody.location = deliveryOverride.location;
        if (deliveryOverride.phoneNumber) requestBody.phoneNumber = deliveryOverride.phoneNumber;
        if (deliveryOverride.nearestLandmark) requestBody.nearestLandmark = deliveryOverride.nearestLandmark;
        if (deliveryOverride.note) requestBody.note = deliveryOverride.note;
      }

      const response = await fetch(
        `${REORDER_API.baseUrl}${REORDER_API.endpoints.regularReorder}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        }
      );
      
      const result = await response.json();
      
      if (!response.ok) {
        handleReorderError({ response: { status: response.status } }, result);
        return;
      }
      
      if (result.success) {
        // Handle both response structures: direct order or nested data
        const order = result.order || result.data?.order;
        const pricing = result.data?.pricing;
        
        if (order) {
          // Create detailed success message with order details
          const mealNames = order?.meals?.map(m => m.meal?.name || m.name).join(', ') || 'items';
          const restaurant = order?.restaurant?.name || 'restaurant';
          const totalPrice = pricing?.finalPrice || order?.totalPrice || 0;
          
          showMessage(
            'success',
            '🎉 Order Placed Successfully!',
            `${mealNames} from ${restaurant}`,
            `Order #${order?.customId} • Total: ₦${totalPrice.toLocaleString()} • Status: ${order?.status || 'Pending'}`
          );
          
          if (onOrderSuccess) {
            onOrderSuccess(order);
          }
          
          // Reset form
          setOrderCustomId('');
          setDeliveryOverride({ location: '', phoneNumber: '', nearestLandmark: '', note: '' });
          setShowDeliveryOverride(false);
          setPreviewData(null);
          setShowPreview(false);
        } else {
          showMessage('error', 'Invalid Response', 'Order data not found in response');
        }
      } else {
        handleReorderError({}, result);
      }
    } catch (error) {
      console.error('Error creating regular reorder:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showMessage('error', 'Network Error', 'Please check your connection and try again.');
      } else {
        showMessage('error', 'Order Failed', error.message || 'Failed to create order');
      }
    } finally {
      setIsExecutingReorder(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'quick') {
      fetchQuickReorders();
    }
  }, [activeTab, fetchQuickReorders]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6 text-cheese" />
        <h2 className="text-xl sm:text-2xl font-bold text-crust">Reorder System</h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6 p-1 bg-secondary-100 rounded-lg">
        <button
          onClick={() => setActiveTab('quick')}
          className={`flex-1 py-2 px-2 sm:px-4 rounded-md font-medium transition-colors text-sm sm:text-base ${
            activeTab === 'quick'
              ? 'bg-cheese text-crust shadow-sm'
              : 'text-olive-light hover:text-crust'
          }`}
        >
          <HeartIcon className="h-4 w-4 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Quick Reorder</span>
          <span className="sm:hidden">Quick</span>
        </button>
        <button
          onClick={() => setActiveTab('regular')}
          className={`flex-1 py-2 px-2 sm:px-4 rounded-md font-medium transition-colors text-sm sm:text-base ${
            activeTab === 'regular'
              ? 'bg-cheese text-crust shadow-sm'
              : 'text-olive-light hover:text-crust'
          }`}
        >
          <ClockIcon className="h-4 w-4 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Regular Reorder</span>
          <span className="sm:hidden">Regular</span>
        </button>
      </div>

      {/* Delivery Override Section - Only for Quick Reorders */}
      {activeTab === 'quick' && (
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="deliveryOverride"
              checked={showDeliveryOverride}
              onChange={(e) => setShowDeliveryOverride(e.target.checked)}
              className="rounded border-gray-300 text-cheese focus:ring-cheese"
            />
            <label htmlFor="deliveryOverride" className="text-sm font-medium text-crust">
              Update delivery details
            </label>
          </div>

        <AnimatePresence>
          {showDeliveryOverride && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-secondary-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-crust mb-1">
                    <MapPinIcon className="h-4 w-4 inline mr-1" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={deliveryOverride.location}
                    onChange={(e) => setDeliveryOverride(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cheese focus:border-cheese"
                    placeholder="New delivery location"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-crust mb-1">
                    <PhoneIcon className="h-4 w-4 inline mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={deliveryOverride.phoneNumber}
                    onChange={(e) => setDeliveryOverride(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cheese focus:border-cheese"
                    placeholder="New phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-crust mb-1">
                    <MapPinIcon className="h-4 w-4 inline mr-1" />
                    Nearest Landmark
                  </label>
                  {universityLandmarks.length > 0 ? (
                    <select
                      value={deliveryOverride.nearestLandmark}
                      onChange={(e) => setDeliveryOverride(prev => ({ ...prev, nearestLandmark: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cheese focus:border-cheese"
                      disabled={landmarksLoading}
                    >
                      <option value="">Select a landmark...</option>
                      {universityLandmarks.map((landmark, idx) => (
                        <option key={idx} value={landmark}>{landmark}</option>
                      ))}
                    </select>
                  ) : landmarksLoading ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                      Loading landmarks...
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={deliveryOverride.nearestLandmark}
                      onChange={(e) => setDeliveryOverride(prev => ({ ...prev, nearestLandmark: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cheese focus:border-cheese"
                      placeholder="Enter nearest landmark"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-crust mb-1">
                    <SpeakerWaveIcon className="h-4 w-4 inline mr-1" />
                    Special Note
                  </label>
                  <input
                    type="text"
                    value={deliveryOverride.note}
                    onChange={(e) => setDeliveryOverride(prev => ({ ...prev, note: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cheese focus:border-cheese"
                    placeholder="Special instructions"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      )}

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* Preview Modal */}
        {showPreview && previewData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            >
              <div className="bg-gradient-to-r from-crust to-crust/90 p-6 text-white">
                <h2 className="text-2xl font-bold">Order Preview</h2>
                <p className="text-cheese/90 text-sm">
                  Review your reorder before placing it
                </p>
              </div>
              
              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
                {/* Restaurant Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-2">Restaurant</h3>
                  <p className="text-lg font-semibold">{previewData.restaurant?.name}</p>
                  <p className="text-sm text-gray-600">{previewData.restaurant?.location}</p>
                </div>

                {/* Available Meals */}
                {previewData.meals?.available?.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-4">
                    <h3 className="font-bold text-green-800 mb-3">Available Items</h3>
                    <div className="space-y-2">
                      {previewData.meals.available.map((meal, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div>
                            <span className="font-medium">{meal.quantity}x {meal.name}</span>
                            <p className="text-sm text-gray-600">{meal.description}</p>
                          </div>
                          <span className="font-bold text-green-600">₦{meal.subtotal?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unavailable Meals */}
                {previewData.meals?.unavailable?.length > 0 && (
                  <div className="bg-red-50 rounded-xl p-4">
                    <h3 className="font-bold text-red-800 mb-3">Unavailable Items</h3>
                    <div className="space-y-2">
                      {previewData.meals.unavailable.map((meal, index) => (
                        <div key={index} className="text-red-700">
                          <span className="font-medium">{meal.name}</span>
                          <p className="text-sm">{meal.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delivery Info */}
                <div className="bg-primary-50 rounded-xl p-4">
                  <h3 className="font-bold text-primary-800 mb-3">Delivery Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Location:</span> {previewData.delivery?.location}</p>
                    <p><span className="font-medium">Phone:</span> {previewData.delivery?.phoneNumber}</p>
                    <p><span className="font-medium">Landmark:</span> {previewData.delivery?.nearestLandmark}</p>
                    {previewData.delivery?.note && (
                      <p><span className="font-medium">Note:</span> {previewData.delivery.note}</p>
                    )}
                  </div>
                  {(previewData.delivery?.locationChanged || previewData.delivery?.phoneChanged || previewData.delivery?.landmarkChanged) && (
                    <div className="mt-3 p-2 bg-yellow-100 rounded text-yellow-800 text-sm">
                      <p className="font-medium">Changes from original:</p>
                      {previewData.delivery.locationChanged && <p>• Location updated</p>}
                      {previewData.delivery.phoneChanged && <p>• Phone number updated</p>}
                      {previewData.delivery.landmarkChanged && <p>• Landmark updated</p>}
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Pricing</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Food Amount:</span>
                      <span>₦{previewData.pricing?.foodAmount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Fee:</span>
                      <span>₦{previewData.pricing?.deliveryFee?.toLocaleString()}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>₦{previewData.pricing?.totalPrice?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Balance Check */}
                <div className={`rounded-xl p-4 ${previewData.user?.hasInsufficientBalance ? 'bg-red-50' : 'bg-green-50'}`}>
                  <h3 className={`font-bold mb-2 ${previewData.user?.hasInsufficientBalance ? 'text-red-800' : 'text-green-800'}`}>
                    Wallet Balance
                  </h3>
                  <p>Current Balance: ₦{previewData.user?.currentBalance?.toLocaleString()}</p>
                  {previewData.user?.hasInsufficientBalance && (
                    <p className="text-red-600 text-sm mt-1">
                      Shortfall: ₦{previewData.user?.balanceShortfall?.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex gap-3">
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                  }}
                  className="flex-1 py-3 px-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold transition-all rounded-xl"
                >
                  Close Preview
                </button>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    executeRegularReorder();
                  }}
                  disabled={!previewData.canProceed?.status || isExecutingReorder}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
                    previewData.canProceed?.status && !isExecutingReorder
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  {isExecutingReorder ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'quick' && (
          <motion.div
            key="quick"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cheese"></div>
                <span className="ml-3 text-gray-600">Loading saved orders...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-gray-500 mb-4">
                  <HeartIcon className="h-12 w-12 mx-auto mb-2" />
                  <p>No saved quick reorders yet</p>
                  <p className="text-sm">Your frequently ordered meals will appear here</p>
                </div>
                <button
                  onClick={fetchQuickReorders}
                  className="text-cheese hover:text-yellow-600 font-medium"
                >
                  Try again
                </button>
              </div>
            ) : savedOrders.length === 0 ? (
              <div className="text-center py-12">
                <HeartIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-2">No saved quick reorders yet</p>
                <p className="text-sm text-gray-400">Your frequently ordered meals will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {savedOrders.map((order) => (
                <motion.div
                  key={order._id}
                  layout
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <HeartSolid className="h-5 w-5 text-red-500" />
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{order.name}</h3>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-gray-600 mb-3">
                        <span className="font-medium">{order.restaurant?.name}</span>
                        <div className="flex items-center gap-4">
                          <span>₦{order.totalPrice?.toLocaleString()}</span>
                          <span>•</span>
                          <span>Used {order.timesOrdered || 0} times</span>
                        </div>
                      </div>

                      {/* Last ordered info */}
                      {order.lastOrdered && (
                        <div className="text-xs text-gray-500 mb-3">
                          Last ordered: {new Date(order.lastOrdered).toLocaleDateString()}
                        </div>
                      )}

                      <div className="space-y-1">
                        {order.meals?.slice(0, 3).map((item, index) => (
                          <div key={index} className="text-sm text-gray-600">
                            {item.quantity}x {item.meal?.name}
                          </div>
                        ))}
                        {order.meals?.length > 3 && (
                          <div className="text-sm text-gray-500">
                            +{order.meals.length - 3} more items
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 sm:ml-4 w-full sm:w-auto">
                      <button
                        onClick={() => executeQuickReorder(order._id, order.name)}
                        disabled={isExecutingReorder || isDeleting === order._id}
                        className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                          isExecutingReorder || isDeleting === order._id
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-cheese text-crust hover:bg-yellow-500'
                        }`}
                      >
                        {isExecutingReorder ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            <span className="hidden sm:inline">Ordering...</span>
                            <span className="sm:hidden">...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <ShoppingBagIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Reorder</span>
                          </div>
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          setReorderToDelete(order);
                          setShowDeleteModal(true);
                        }}
                        disabled={isExecutingReorder || isDeleting === order._id}
                        className={`p-2 rounded-lg font-medium transition-colors ${
                          isExecutingReorder || isDeleting === order._id
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        title="Delete Quick Reorder"
                      >
                        {isDeleting === order._id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'regular' && (
          <motion.div
            key="regular"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order ID
                </label>
                <input
                  type="text"
                  value={orderCustomId}
                  onChange={(e) => setOrderCustomId(e.target.value)}
                  placeholder="Enter the order ID you want to reorder"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Delivery Override Section for Regular Reorder */}
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-primary-800">
                    Override Delivery Details (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDeliveryOverride(!showDeliveryOverride)}
                    className="text-xs bg-primary-100 hover:bg-primary-200 text-primary-700 px-3 py-1 rounded-full transition-all"
                  >
                    {showDeliveryOverride ? 'Hide' : 'Show'} Options
                  </button>
                </div>
                
                {showDeliveryOverride && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <MapPinIcon className="h-4 w-4 inline mr-1" />
                          Location
                        </label>
                        <input
                          type="text"
                          value={deliveryOverride.location}
                          onChange={(e) => setDeliveryOverride(prev => ({ ...prev, location: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="New delivery location"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <PhoneIcon className="h-4 w-4 inline mr-1" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={deliveryOverride.phoneNumber}
                          onChange={(e) => setDeliveryOverride(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="New phone number"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <MapPinIcon className="h-4 w-4 inline mr-1" />
                          Nearest Landmark
                        </label>
                        {universityLandmarks.length > 0 ? (
                          <select
                            value={deliveryOverride.nearestLandmark}
                            onChange={(e) => setDeliveryOverride(prev => ({ ...prev, nearestLandmark: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            disabled={landmarksLoading}
                          >
                            <option value="">Select a landmark...</option>
                            {universityLandmarks.map((landmark, idx) => (
                              <option key={idx} value={landmark}>{landmark}</option>
                            ))}
                          </select>
                        ) : landmarksLoading ? (
                          <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                            Loading landmarks...
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={deliveryOverride.nearestLandmark}
                            onChange={(e) => setDeliveryOverride(prev => ({ ...prev, nearestLandmark: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter nearest landmark"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <ChatBubbleLeftIcon className="h-4 w-4 inline mr-1" />
                          Note
                        </label>
                        <input
                          type="text"
                          value={deliveryOverride.note}
                          onChange={(e) => setDeliveryOverride(prev => ({ ...prev, note: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Special instructions or note"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded-md">
                      💡 Leave fields empty to use your profile defaults or the original order's details
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={previewReorder}
                  disabled={!orderCustomId.trim() || isPreviewLoading}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all text-sm sm:text-base ${
                    orderCustomId.trim() && !isPreviewLoading
                      ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isPreviewLoading ? 'Loading Preview...' : 'Preview Order'}
                </button>
                
                <button
                  onClick={executeRegularReorder}
                  disabled={!orderCustomId.trim() || isExecutingReorder}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all text-sm sm:text-base ${
                    orderCustomId.trim() && !isExecutingReorder
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  {isExecutingReorder ? 'Placing Order...' : 'Reorder Now'}
                </button>
              </div>
              
              <div className="text-sm text-gray-600">
                <p className="mb-2">💡 Pro Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Use Preview to check availability, pricing, and see what changed</li>
                  <li>Your current profile information will be used by default</li>
                  <li>Override delivery details to send to a different location or update your note</li>
                  <li>Leave override fields empty to use your profile details</li>
                  <li>Unavailable items will be reported before ordering</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Message Display */}
      <MessageDisplay message={message} onClose={() => setMessage(null)} />
      
      {/* Order Loading Screen */}
      {isOrderLoading && (
        <OrderLoader 
          message="Processing your reorder..."
          details="Please wait while we prepare your order"
        />
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && reorderToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-4">
                <TrashIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Delete Quick Reorder</h3>
              </div>

              {/* Reorder Details */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <HeartSolid className="h-4 w-4 text-red-500" />
                  <span className="font-medium">{reorderToDelete.name}</span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Restaurant:</span>
                    <span>{reorderToDelete.restaurant?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Price:</span>
                    <span>₦{reorderToDelete.totalPrice?.toLocaleString()}</span>
                  </div>
                  {reorderToDelete.timesOrdered && (
                    <div className="flex justify-between">
                      <span>Times Used:</span>
                      <span className="text-green-600">{reorderToDelete.timesOrdered}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-sm text-gray-600 mb-4">
                <p className="mb-2">
                  Are you sure you want to delete this quick reorder? This action cannot be undone.
                </p>
                <p className="text-xs text-gray-500">
                  Your order history will remain intact, but you won't be able to quickly reorder this combination anymore.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setReorderToDelete(null);
                  }}
                  disabled={isDeleting === reorderToDelete._id}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteQuickReorder(reorderToDelete._id)}
                  disabled={isDeleting === reorderToDelete._id}
                  className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isDeleting === reorderToDelete._id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <TrashIcon className="h-4 w-4" />
                      <span>Delete</span>
                    </div>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReorderSystem;
